// askSmart-ui/src/utils/smrtdocsFile.ts
//
// Resolves a SMRT Docs blob for in-browser viewing through the backend proxy
// and returns a `blob:` object URL the browser can render natively.
//
// Why the proxy: a network firewall on the storage account blocks browsers
// from reaching *.blob.core.windows.net directly, so the only supported way to
// read a file is to have the backend (whose IPs are allowlisted) fetch the
// bytes and stream them back. The endpoint's `?format=base64` branch does
// exactly that — direct SAS downloads now return 400.
//
// Why a blob: object URL and not a base64 data: URI: an earlier viewer built a
// `data:application/pdf;base64,…` URI and set it as an <iframe src>. base64
// inflates the payload ~33% and, once a document crosses a few MB, the data URI
// exceeds the browser's data-URI size cap for iframe navigation — the iframe
// silently loads nothing (blank popout). An object URL is a short reference
// with no size cap, streams into the browser's native PDF viewer (print /
// download toolbar included), and uses far less memory.
//
// The returned object URL is intentionally not revoked here: callers hand it
// straight to window.open() or a long-lived in-page <iframe>, so we let the
// browser release it on document unload rather than risk revoking a URL that
// is still being viewed.

/**
 * Fetch a SMRT Docs blob owned by `ownerUserId` through the backend proxy and
 * return a `blob:` object URL for rendering. Throws if the request fails or the
 * response carries no content.
 */
export async function fetchSmrtDocsFileUrl(
  ownerUserId: string,
  blobName: string,
): Promise<string> {
  const res = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/smrtdocs/files/${encodeURIComponent(
      ownerUserId,
    )}/_?blobName=${encodeURIComponent(blobName)}&format=base64`,
    { credentials: "include" },
  );
  if (!res.ok) throw new Error(`Failed to get file (${res.status})`);

  const { base64, contentType } = await res.json();
  if (!base64) throw new Error("File response missing base64 content");

  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const blob = new Blob([bytes], { type: contentType || "application/pdf" });

  return URL.createObjectURL(blob);
}

/**
 * Backend URL that streams a document as an ordinary PDF over https.
 *
 * Prefer this over fetch + blob: for anything the user is meant to *view*.
 * A managed/corporate browser (the case that prompted this: Scotiabank) will
 * routinely refuse to navigate to a blob: URL, which presents as a silent blank
 * tab — the bytes arrive, the browser simply will not display them, and there
 * is no error to catch. A plain https URL has none of that fragility: it opens
 * in the browser's built-in PDF viewer, Print/Save work, and corporate proxies
 * and DLP can inspect it like any other download.
 *
 * Auth rides along on the httpOnly access_token cookie, which the browser sends
 * on a top-level navigation exactly as it does on a fetch — so no token needs to
 * be put in the URL.
 */
export function smrtDocsFileViewUrl(
  ownerUserId: string,
  blobName: string,
): string {
  return `${process.env.REACT_APP_BACKEND_URL}/smrtdocs/files/${encodeURIComponent(
    ownerUserId,
  )}/_?blobName=${encodeURIComponent(blobName)}&format=inline`;
}

// Set as a real <a href> for a navigational download (Content-Disposition:
// attachment). Must stay a genuine user-clicked anchor — RBI / managed
// browsers suppress synthetic clicks on script-created elements.
export function smrtDocsFileDownloadUrl(
  ownerUserId: string,
  blobName: string,
): string {
  return `${smrtDocsFileViewUrl(ownerUserId, blobName)}&disposition=attachment`;
}

// Certificate of Document Execution for an issued doc, generated server-side
// from stored state and served with Content-Disposition: attachment. Set as a
// real <a href> — same rule as smrtDocsFileDownloadUrl above.
// Pass serviceId whenever it is known: Sub Services share their Master's field
// keys, so without it the server takes the first service holding the key —
// possibly a sibling tenancy's document.
export function smrtDocsCertificateUrl(
  ownerUserId: string,
  issuedKey: string,
  serviceId?: string,
): string {
  return `${process.env.REACT_APP_BACKEND_URL}/smrtdocs/certificate/${encodeURIComponent(
    ownerUserId,
  )}/${encodeURIComponent(issuedKey)}${
    serviceId ? `?serviceId=${encodeURIComponent(serviceId)}` : ""
  }`;
}

/**
 * Open a SMRT Docs document in a new tab, and report whether the tab was
 * actually created.
 *
 * Synchronous by design. Nothing is awaited before window.open(), so the click's
 * user activation is still live and popup blockers have no reason to intervene —
 * that was the other half of the original "clicking does nothing" bug, where the
 * open happened only after a multi-megabyte fetch had resolved.
 *
 * `opened: false` means the browser refused the tab outright; the caller should
 * render the document in-page rather than assume a tab appeared. Failures to
 * fetch the document now surface in that tab as a normal HTTP error response
 * instead of vanishing into a console log.
 */
export function openSmrtDocsFile(
  ownerUserId: string,
  blobName: string,
): { opened: boolean; url: string } {
  const url = smrtDocsFileViewUrl(ownerUserId, blobName);
  const win = window.open(url, "_blank");
  return { opened: !!win, url };
}

/**
 * Save a resolved blob: URL to disk via a synthetic anchor click.
 *
 * NATIVE-APP ONLY (Capacitor mobile viewer). Do NOT use this on any surface a
 * desktop browser can reach: RBI / managed browsers suppress synthetic clicks
 * on script-created anchors, so this pattern silently does nothing there. For
 * browser-facing downloads, render a real <a href> to a backend URL that
 * responds with Content-Disposition: attachment.
 */
export function downloadBlobUrl(url: string, fileName: string): void {
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName || "document.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
}
